import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ClipButton } from './ReportWorkspace';
import '../styles/TerritoryView.css';
import filterParams from '../utils/filterParams';

const number = (value) => Number(value || 0);
const CONTEXT_LABELS = {
  problem_reported: 'Problem reported',
  risk_or_prevention: 'Risk or prevention discussed',
  no_current_problem: 'No current problem reported',
  context_to_review: 'Context to review',
};
const same = (a, b) =>
  a &&
  b &&
  a.field_hq_code === b.field_hq_code &&
  a.recorded_province === b.recorded_province &&
  a.crop === b.crop &&
  a.issue === b.issue &&
  a.context_type === b.context_type;
const date = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? String(value).slice(0, 10)
    : parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function TerritoryView({ filters, refreshKey }) {
  const [hqCode, setHqCode] = useState('');
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const params = useMemo(() => filterParams(filters, { hq_code: hqCode }), [filters, hqCode]);
  const queryKey = params.toString();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setSelected(null);
    setEvidence(null);
    axios
      .get(`/api/agent-view/territory?${queryKey}`)
      .then((response) => {
        if (active) {
          setData(response.data);
          setSelected(response.data.priorities?.[0] || null);
        }
      })
      .catch((error) => {
        if (active) setError(error.response?.data?.error || 'Territory data could not be loaded.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [queryKey, refreshKey]);

  useEffect(() => {
    if (!selected) return;
    let active = true;
    setEvidence(null);
    const detail = new URLSearchParams(params);
    detail.set('hq_code', selected.field_hq_code);
    detail.set('province', selected.recorded_province);
    detail.set('crop', selected.crop);
    detail.set('issue', selected.issue);
    detail.set('context_type', selected.context_type);
    axios
      .get(`/api/agent-view/territory/evidence?${detail}`)
      .then((response) => {
        if (active) setEvidence(response.data);
      })
      .catch(() => {
        if (active) setEvidence({ comments: [], context: [] });
      });
    return () => {
      active = false;
    };
  }, [selected, params]);

  const activity = Object.fromEntries((data?.activity || []).map((row) => [String(row.field_hq_code), row]));
  const placeCoverage = Object.fromEntries(
    (data?.place_coverage || []).map((row) => [String(row.field_hq_code), row]),
  );
  const priorities = data?.priorities || [];
  // Org switches (Admin -> Configuration); each is off unless the org turns it on.
  const cropOn = !!data?.switches?.crop_issue_review;
  const placeOn = !!data?.switches?.place_matching;
  const rows = priorities.map((row) => ({
    label: `${row.recorded_province} · ${row.crop} · ${row.issue} · ${CONTEXT_LABELS[row.context_type] || 'Context to review'}`,
    value: `${row.supporting_conversations} reviewed conversation${number(row.supporting_conversations) === 1 ? '' : 's'}; last heard ${date(row.last_mentioned)}`,
    status: 'none',
  }));
  const selectedWeeks = (data?.weekly || []).filter((row) => same(row, selected));
  const maxWeekly = Math.max(1, ...selectedWeeks.map((row) => number(row.conversations)));

  return (
    <div className="territory-view">
      <div className="territory-intro">
        <div>
          <h3>Territory</h3>
          <p>
            Use field conversations to prepare visits and see where the team is recording market feedback.
          </p>
        </div>
        <label>
          Field team area
          <select value={hqCode} onChange={(event) => setHqCode(event.target.value)}>
            <option value="">All areas</option>
            {(data?.areas || []).map((area) => (
              <option key={area.field_hq_code} value={area.field_hq_code}>
                {area.field_hq_name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error && (
        <div className="decision-empty" role="alert">
          {error}
        </div>
      )}
      {loading && (
        <div className="decision-empty" role="status">
          Loading territory view…
        </div>
      )}
      {!loading && data && (
        <>
          <p className="territory-period">
            Latest recording: {date(data.latest_date)}. “Recent” means the latest four recorded weeks;
            “earlier” means the four weeks before that, within the selected date filter.
          </p>
          {cropOn && (
            <section className="territory-card">
              <header>
                <div>
                  <h3>What conversations say about crop issues</h3>
                  <p>
                    Only reviewed crop and issue links appear here. Each row says whether a problem was
                    reported, a risk was discussed, or no current problem was reported.
                  </p>
                </div>
                <ClipButton item={{ title: 'Reviewed crop issues by territory', visual: 'table', rows }} />
              </header>
              {priorities.length ? (
                <div className="territory-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Field team area</th>
                        <th>Recorded province</th>
                        <th>Crop</th>
                        <th>Issue</th>
                        <th>What was said</th>
                        <th>Conversations</th>
                        <th>Recent 4 weeks</th>
                        <th>Earlier 4 weeks</th>
                        <th>Last heard</th>
                        <th>Next step</th>
                      </tr>
                    </thead>
                    <tbody>
                      {priorities.map((row, index) => {
                        const recent = number(row.recent_conversations),
                          earlier = number(row.earlier_conversations),
                          support = number(row.supporting_conversations);
                        const readout =
                          row.context_type === 'problem_reported'
                            ? recent === 0
                              ? 'Not heard recently'
                              : recent < 3 || number(row.recent_contributors) < 2
                                ? 'Verify locally'
                                : recent > earlier
                                  ? 'More reports'
                                  : recent < earlier
                                    ? 'Fewer reports'
                                    : 'Steady'
                            : row.context_type === 'risk_or_prevention'
                              ? 'Ask on next visit'
                              : row.context_type === 'no_current_problem'
                                ? 'No current action'
                                : 'Review context';
                        return (
                          <tr
                            key={`${row.field_hq_code}-${row.recorded_province}-${row.crop}-${row.issue}-${row.context_type}-${index}`}
                            className={same(row, selected) ? 'selected' : ''}
                            onClick={() => setSelected(row)}
                            tabIndex="0"
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                setSelected(row);
                              }
                            }}
                          >
                            <td>{row.field_hq_name}</td>
                            <td>{row.recorded_province}</td>
                            <td>{row.crop}</td>
                            <td>{row.issue}</td>
                            <td>
                              <span className={`territory-context-tag ${row.context_type}`}>
                                {CONTEXT_LABELS[row.context_type] || 'Context to review'}
                              </span>
                            </td>
                            <td>{support}</td>
                            <td>{recent}</td>
                            <td>{earlier}</td>
                            <td>{date(row.last_mentioned)}</td>
                            <td>
                              <span
                                className={`territory-status ${readout === 'More reports' ? 'rising' : ''}`}
                              >
                                {readout}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="territory-empty">
                  No reviewed crop–issue links in this selection yet. Nearby mentions wait in the Review Queue
                  for an admin; they are not findings.
                </p>
              )}
            </section>
          )}
          {selected && (
            <section className="territory-card">
              <header>
                <div>
                  <h3>
                    {selected.context_type === 'problem_reported'
                      ? 'What to check on the next visit'
                      : selected.context_type === 'risk_or_prevention'
                        ? 'What to ask on the next visit'
                        : selected.context_type === 'no_current_problem'
                          ? 'What was reported as safe'
                          : 'What was discussed'}
                  </h3>
                  <p>
                    {selected.crop} · {selected.issue} · {selected.recorded_province}.{' '}
                    {CONTEXT_LABELS[selected.context_type] || 'Context to review'}. Read the source wording
                    below before deciding what to do.
                  </p>
                </div>
              </header>
              <div className="territory-weekly">
                <h4>When this was heard</h4>
                {selectedWeeks.map((week, index) => (
                  <div key={index}>
                    <span>Week of {date(week.week_start)}</span>
                    <i>
                      <b style={{ width: `${(100 * number(week.conversations)) / maxWeekly}%` }} />
                    </i>
                    <strong>{week.conversations}</strong>
                  </div>
                ))}
              </div>
              <div className="territory-detail">
                <div>
                  <h4>Also mentioned</h4>
                  {evidence ? (
                    evidence.context?.length ? (
                      <div className="territory-context">
                        {evidence.context.map((item, index) => (
                          <div key={index}>
                            <strong>{item.entity_name}</strong>
                            <span>
                              {item.entity_type} · {item.conversations} conversation
                              {number(item.conversations) === 1 ? '' : 's'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>No product or company was identified in these conversations.</p>
                    )
                  ) : (
                    <p>Loading context…</p>
                  )}
                </div>
                <div>
                  <h4>Recorded wording</h4>
                  {evidence ? (
                    evidence.comments?.length ? (
                      evidence.comments.map((item, index) => (
                        <article className="territory-comment" key={`${item.conversation_id}-${index}`}>
                          <p>“{item.crop_comment}”</p>
                          {item.issue_comment !== item.crop_comment && <p>“{item.issue_comment}”</p>}
                          <small>
                            {date(item.call_date)} · {item.recorded_location} · {item.agent_name} ·
                            Conversation #{item.conversation_id}
                          </small>
                        </article>
                      ))
                    ) : (
                      <p>No quoted comments are available for this selection.</p>
                    )
                  ) : (
                    <p>Loading comments…</p>
                  )}
                </div>
              </div>
            </section>
          )}
          <section className="territory-card">
            <header>
              <div>
                <h3>Where the field team is contributing</h3>
                <p>
                  Assigned field-team area from onboarding; conversation location is shown separately below.
                </p>
              </div>
              <ClipButton
                item={{
                  title: 'Territory contribution by field team area',
                  visual: 'table',
                  rows: (data.areas || []).map((area) => ({
                    label: area.field_hq_name,
                    value: `${activity[String(area.field_hq_code)]?.conversations || 0} conversations · ${activity[String(area.field_hq_code)]?.contributors || 0} contributors of ${area.assigned_agents} assigned`,
                    status: 'none',
                  })),
                }}
              />
            </header>
            <div className="territory-coverage">
              {(data.areas || [])
                .filter((area) => !hqCode || String(area.field_hq_code) === hqCode)
                .map((area) => {
                  const recorded = activity[String(area.field_hq_code)] || {};
                  const coverage = placeCoverage[String(area.field_hq_code)] || {};
                  const provinces = (data.places || []).filter(
                    (place) => String(place.field_hq_code) === String(area.field_hq_code),
                  );
                  return (
                    <div key={area.field_hq_code}>
                      <b>{area.field_hq_name}</b>
                      <span>
                        {recorded.conversations || 0} conversations · {recorded.contributors || 0} of{' '}
                        {area.assigned_agents} assigned people contributed
                      </span>
                      {placeOn && (
                        <>
                          <span>
                            {coverage.matched_subdistricts || 0} of {coverage.assigned_subdistricts || 0}{' '}
                            assigned sub-districts have a matched recording
                          </span>
                          <small>
                            {coverage.locations_needing_review || 0} recorded place names await review
                          </small>
                        </>
                      )}
                      <small>
                        {provinces.length
                          ? provinces
                              .map((place) => `${place.recorded_province}: ${place.conversations}`)
                              .join(' · ')
                          : 'No recording location in this selection'}
                      </small>
                    </div>
                  );
                })}
            </div>
            {placeOn && (
              <p className="territory-caution">
                A matched recording is not proof of a visit to every part of a sub-district. An unmatched
                place is not evidence that no visit happened.
              </p>
            )}
            {(data.place_coverage || [])
              .filter((area) => !hqCode || String(area.field_hq_code) === hqCode)
              .some((area) => area.matched_places?.length > 0) && (
              <details className="territory-matched">
                <summary>See matched sub-districts</summary>
                {(data.place_coverage || [])
                  .filter((area) => !hqCode || String(area.field_hq_code) === hqCode)
                  .flatMap((area) =>
                    area.matched_places.map((place) => (
                      <div key={`${area.field_hq_code}-${place.subdistrict_code}`}>
                        <strong>{place.subdistrict_name}</strong>
                        <span>
                          {place.district_name} · {place.conversations} conversation
                          {number(place.conversations) === 1 ? '' : 's'} ·{' '}
                          {place.match_source === 'reviewed' ? 'Admin reviewed' : 'Exact name match'}
                        </span>
                      </div>
                    )),
                  )}
              </details>
            )}
          </section>
        </>
      )}
    </div>
  );
}
